import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import { Dashboard } from 'models/dashboard';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import toast from 'react-hot-toast';
import TableHeader from 'components/TableHeader';
import { formatFilter, getDashboardRelativeUrl } from 'utils/format';
import {
    createColumnHelper,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import Table from 'components/Table';

interface DashboardTableProps {
    dashboards: Dashboard[];
    allowCreate: boolean;
}

const dashboardColumnHelper = createColumnHelper<Dashboard>();

const getDashboardColumns = () => [
    dashboardColumnHelper.accessor('name', {
        header: 'Name',
        cell: (props) => (
            <Link to={getDashboardRelativeUrl(props.row.original.id)} title="view dashboard">
                {props.row.original.name}
            </Link>
        ),
    }),
    dashboardColumnHelper.accessor((row) => (row.disabled ? 'disabled' : 'enabled'), {
        header: 'Status',
    }),
    dashboardColumnHelper.accessor((row) => formatFilter(row.filters.operators), {
        header: 'Operators',
    }),
    dashboardColumnHelper.accessor((row) => formatFilter(row.filters.airports), {
        header: 'Airports',
    }),
    dashboardColumnHelper.accessor((row) => formatFilter(row.filters.registrations), {
        header: 'Registrations',
    }),
    dashboardColumnHelper.accessor('id', {
        header: 'Manage',
        cell: (props) => (
            <>
                <Link to={`/admin/dashboards/${props.row.original.id}/edit`} title="manage dashboard">
                    manage
                </Link>
                &nbsp;|&nbsp;
                <CopyToClipboard
                    text={new URL(getDashboardRelativeUrl(props.row.original.id), document.baseURI).href}
                    onCopy={() => {
                        toast.success('Dashboard link copied');
                    }}
                >
                    <a href="#" title="copy dashboard URL" onClick={(event) => event.preventDefault()}>
                        copy URL
                    </a>
                </CopyToClipboard>
            </>
        ),
        enableSorting: false,
        enableGlobalFilter: false,
    }),
];

const DashboardTable: FC<DashboardTableProps> = (props) => {
    const table = useReactTable({
        data: props.dashboards,
        columns: getDashboardColumns(),
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 5,
            },
        },
    });

    return (
        <>
            <TableHeader tableType="dashboard" allowCreate={props.allowCreate} />
            {props.dashboards.length ? <Table table={table} type="dashboard" /> : <div>No dashboards found</div>}
        </>
    );
};

export default DashboardTable;
